import { useState, useEffect } from 'react';
import { X, Radio, Users, MapPin, AlertCircle } from 'lucide-react';
import { useBusinesses } from '../../service-providers/hooks/useBusinesses';
import type { Business } from '../../service-providers/business';

interface BroadcastQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBroadcast: (contractorIds: string[]) => Promise<void>;
  postalCode: string;
  serviceName: string;
}

export default function BroadcastQuoteModal({
  isOpen,
  onClose,
  onBroadcast,
  postalCode,
  serviceName
}: BroadcastQuoteModalProps) {
  const { getBusinessesByPostalCodeAndService } = useBusinesses();
  const [selectedContractors, setSelectedContractors] = useState<string[]>([]);
  const [broadcasting, setBroadcasting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableContractors, setAvailableContractors] = useState<Business[]>([]);
  const [loadingContractors, setLoadingContractors] = useState(false);

  // Efficiently fetch contractors who service the Postal code and provide the requested service
  useEffect(() => {
    if (!postalCode || !serviceName) {
      setAvailableContractors([]);
      return;
    }

    const fetchContractors = async () => {
      try {
        setLoadingContractors(true);
        setError(null);
        
        // Use the efficient method that filters by both postal code and service using database indexes
        console.log(`Fetching contractors for postal code "${postalCode}" offering service "${serviceName}"`);
        const contractors = await getBusinessesByPostalCodeAndService(postalCode, serviceName);
        setAvailableContractors(contractors);
      } catch (err) {
        console.error('Error fetching contractors:', err);
        setError('Failed to load contractors for this area');
        setAvailableContractors([]);
      } finally {
        setLoadingContractors(false);
      }
    };

    fetchContractors();
  }, [postalCode, serviceName, getBusinessesByPostalCodeAndService]);

  const handleToggleContractor = (contractorId: string) => {
    setSelectedContractors(prev => {
      if (prev.includes(contractorId)) {
        return prev.filter(id => id !== contractorId);
      } else {
        return [...prev, contractorId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedContractors.length === availableContractors.length) {
      setSelectedContractors([]);
    } else {
      setSelectedContractors(availableContractors.map(c => c.id));
    }
  };

  const handleBroadcast = async () => {
    if (selectedContractors.length === 0) {
      setError('Please select at least one contractor');
      return;
    }

    try {
      setBroadcasting(true);
      setError(null);
      await onBroadcast(selectedContractors);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to broadcast quote');
    } finally {
      setBroadcasting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onClose();
            }
          }}
          tabIndex={0}
          role="button"
          aria-label="Close modal"
        />

        {/* Modal panel */}
        <div className="modal-broadcast-quote inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Radio className="h-6 w-6 text-white" />
                <h3 className="text-xl font-semibold text-white">
                  Broadcast Quote to Service Providers
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Postal Code Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Service Details</p>
                  <p className="text-sm text-blue-700 mt-1">
                    <span className="font-semibold">Service:</span> {serviceName || 'Not specified'}
                  </p>
                  {postalCode && (
                    <p className="text-sm text-blue-600 mt-2">
                      <span className="font-semibold">Postal Code:</span> {postalCode}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* No Postal code error */}
            {!postalCode && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900">Unable to Extract Postal Code</p>
                    <p className="text-sm text-red-700 mt-1">
                      The address does not contain a valid Postal code. Please update the quote address before broadcasting.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* No service selected error */}
            {postalCode && !serviceName && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900">Service Not Specified</p>
                    <p className="text-sm text-red-700 mt-1">
                      The quote does not have a service selected. Please specify the service before broadcasting.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading service providers */}
            {postalCode && serviceName && loadingContractors && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mt-0.5"></div>
                  <div>
                    <p className="font-medium text-blue-900">Loading Service Providers</p>
                    <p className="text-sm text-blue-700 mt-1">
                      Searching for service providers offering <span className="font-semibold">{serviceName}</span> in postal code <span className="font-semibold">{postalCode}</span>...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* No service providers available */}
            {postalCode && serviceName && !loadingContractors && availableContractors.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-900">No Service Providers Available</p>
                    <p className="text-sm text-yellow-700 mt-1">
                      No service providers currently offer <span className="font-semibold">{serviceName}</span> in postal code <span className="font-semibold">{postalCode}</span>. 
                      Please update service provider services or service areas before broadcasting.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Service Providers list */}
            {postalCode && serviceName && !loadingContractors && availableContractors.length > 0 && (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-700">
                    <Users className="h-4 w-4 inline mr-1" />
                    Showing <span className="font-bold text-indigo-600">{availableContractors.length}</span> service provider{availableContractors.length !== 1 ? 's' : ''} offering <span className="font-bold">{serviceName}</span> in postal code <span className="font-bold">{postalCode}</span>
                  </p>
                  <button
                    onClick={handleSelectAll}
                    className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    {selectedContractors.length === availableContractors.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-80 overflow-y-auto">
                  {availableContractors.map((contractor) => (
                    <label
                      key={contractor.id}
                      data-contractor-id={contractor.id}
                      className="flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer transition"
                    >
                      <input
                        type="checkbox"
                        value={contractor.id}
                        checked={selectedContractors.includes(contractor.id)}
                        onChange={() => handleToggleContractor(contractor.id)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{contractor.businessName}</p>
                        <p className="text-sm text-gray-500">{contractor.email}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Services {contractor.servicePostalCodes?.length || 0} Postal code{(contractor.servicePostalCodes?.length || 0) !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}

            {/* Error message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-end gap-3 border-t border-gray-200">
            <button
              onClick={onClose}
              disabled={broadcasting}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleBroadcast}
              disabled={broadcasting || loadingContractors || !postalCode || !serviceName || availableContractors.length === 0 || selectedContractors.length === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
            >
              <Radio className="h-4 w-4" />
              {broadcasting ? 'Broadcasting...' : `Broadcast to ${selectedContractors.length} Contractor${selectedContractors.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
